/**
 * Apply server playback snapshots to local player + audio element.
 * Progress is derived from absolute timeline anchors (see playbackClock).
 */
import type { PlaybackSnapshot, QueueEntry, Track } from "@memes/shared";
import { applyVoiceOutputMode, mediaUrl, usePlayer } from "../state/player";
import { resumePlaybackAudioContext } from "./audioGraphRuntime";
import { clientLog } from "./clientLogger";
import {
  interpolatePosition,
  mergeClockOffset,
  resolveDisplayedPosition,
  shouldHardSeekAudio,
} from "./playbackClock";

const LOCAL_CONTROL_MS = 1_500;

let applyingRemote = false;
let lastRevision = -1;
let lastTimelineRevision = 0;
let latestSnapshot: PlaybackSnapshot | null = null;
let tracksCatalog = new Map<number, Track>();
/** Ignore remote snapshots briefly after a user transport action (play/pause/skip). */
let localControlUntilMs = 0;
let clockOffsetMs = 0;
let clockRttMs: number | null = null;

export function isApplyingRemotePlayback(): boolean {
  return applyingRemote;
}

function isUnderLocalControl(): boolean {
  return Date.now() < localControlUntilMs;
}

/** Call when opening a new SSE connection so server restarts are not ignored. */
export function resetPlaybackSyncRevision(): void {
  lastRevision = -1;
  lastTimelineRevision = 0;
}

export function markLocalPlaybackControl(): void {
  localControlUntilMs = Date.now() + LOCAL_CONTROL_MS;
}

export function clearLocalPlaybackControl(): void {
  localControlUntilMs = 0;
}

/** True when the server session is paused on this queue index (not a fresh start). */
export function shouldResumePlayback(index: number): boolean {
  const track = usePlayer.getState().queue[index];
  if (!track || !latestSnapshot?.nowPlaying) return false;
  return (
    latestSnapshot.isPaused &&
    latestSnapshot.nowPlaying.id === track.id &&
    latestSnapshot.currentIndex === index
  );
}

/** Updated by usePlaybackSync whenever the library list changes. */
export function setPlaybackTracksCatalog(tracks: readonly Track[]): void {
  tracksCatalog = new Map(tracks.map((t) => [t.id, t]));
  if (latestSnapshot && !isUnderLocalControl()) {
    applyPlaybackSnapshot(latestSnapshot, tracksCatalog);
  }
}

/** Update clock offset from a REST/SSE sample with known RTT. */
export function noteServerClockSample(opts: {
  clientSendMs: number;
  serverTimeMs: number;
  clientRecvMs: number;
}): void {
  const merged = mergeClockOffset(clockOffsetMs, opts, clockRttMs);
  clockOffsetMs = merged.offsetMs;
  clockRttMs = merged.rttMs;
}

export function getLatestPlaybackSnapshot(): PlaybackSnapshot | null {
  return latestSnapshot;
}

export function getClockOffsetMs(): number {
  return clockOffsetMs;
}

function queueEntryToTrack(entry: QueueEntry, full?: Track): Track | null {
  if (full) return full;
  if (!entry.filename) return null;
  return {
    id: entry.id,
    name: entry.title,
    filename: entry.filename,
    owner: entry.owner,
    type: "application/octet-stream",
    addedAt: 0,
    durationSec: entry.durationSec ?? null,
    metadataTitle: entry.title,
    metadataArtist: entry.artist,
    lastPlayed: null,
    loudnessGainDb: null,
  };
}

export function entriesToTracks(
  snap: PlaybackSnapshot,
  tracksById: Map<number, Track>,
): Track[] {
  const out: Track[] = [];
  for (const e of snap.queue) {
    const t = queueEntryToTrack(e, tracksById.get(e.id));
    if (t) out.push(t);
  }
  return out;
}

/** Session intent owns play/pause; document focus must not gate background car audio. */
export function shouldAttemptElementPlay(opts: {
  sessionPlaying: boolean;
  voiceConnected: boolean;
  documentHasFocus?: boolean;
}): boolean {
  return opts.sessionPlaying && !opts.voiceConnected;
}

function syncAudioElement(
  snap: PlaybackSnapshot,
  track: Track | undefined,
  isPlaying: boolean,
  targetSec: number,
  timelineChanged: boolean,
  _userInitiated = false,
): void {
  const s = usePlayer.getState();
  const el = s.audio;
  if (!el || !track?.filename) {
    if (!isPlaying) el?.pause();
    return;
  }

  const target = new URL(mediaUrl(track), window.location.href).href;
  if (el.src !== target) {
    el.src = mediaUrl(track);
  }
  applyVoiceOutputMode(el, s.voiceConnected, 1, s.muted);

  if (
    shouldHardSeekAudio({
      elementSec: el.currentTime,
      targetSec,
      timelineChanged,
    })
  ) {
    el.currentTime = targetSec;
  }
  el.playbackRate = 1;

  if (
    shouldAttemptElementPlay({
      sessionPlaying: isPlaying,
      voiceConnected: s.voiceConnected,
    })
  ) {
    resumePlaybackAudioContext();
    void el.play().catch(() => {
      clientLog.warn("playback", "Browser autoplay blocked");
    });
  } else {
    el.pause();
  }
}

/** Start/stop browser audio synchronously inside a click handler (autoplay policy). */
export function syncBrowserTransport(
  mode: "play" | "pause",
  opts?: { index?: number; currentTime?: number },
): void {
  const s = usePlayer.getState();
  if (s.voiceConnected || !s.audio) return;

  const idx = typeof opts?.index === "number" ? opts.index : s.index;
  const track = s.queue[idx];
  if (!track?.filename) return;

  const el = s.audio;
  const target = new URL(mediaUrl(track), window.location.href).href;
  if (el.src !== target) {
    el.src = mediaUrl(track);
  }
  applyVoiceOutputMode(el, false, 1, s.muted);

  if (typeof opts?.currentTime === "number") {
    el.currentTime = opts.currentTime;
  }
  el.playbackRate = 1;

  if (mode === "play") {
    resumePlaybackAudioContext();
    void el.play().catch((err) => {
      clientLog.error("playback", "Browser play failed", { err: String(err) });
    });
  } else {
    el.pause();
  }
}

export function applyPlaybackSnapshot(
  snap: PlaybackSnapshot,
  tracksById: Map<number, Track> = tracksCatalog,
  userInitiated = false,
): void {
  if (!userInitiated && isUnderLocalControl()) {
    if (snap.normalization) {
      usePlayer.setState({
        normalize: snap.normalization.enabled,
        normalizeGainDb: snap.normalization.preampDb,
      });
    }
    return;
  }

  latestSnapshot = snap;

  if (snap.revision < lastRevision && lastRevision >= 0) return;

  const queue = entriesToTracks(snap, tracksById);
  const hasServerQueue = snap.queue.length > 0;
  if (hasServerQueue && queue.length === 0) return;

  lastRevision = Math.max(lastRevision, snap.revision);

  const index = Math.min(
    snap.currentIndex,
    Math.max(0, hasServerQueue ? queue.length - 1 : 0),
  );
  const track = queue[index];
  const isPlaying = !snap.isPaused && !!snap.nowPlaying && !!track;

  const prev = usePlayer.getState();
  const prevTrack = prev.queue[prev.index];
  const trackChanged = track?.id !== prevTrack?.id;
  const timelineChanged =
    (snap.timelineRevision ?? 1) !== lastTimelineRevision || trackChanged;

  const interpolated = interpolatePosition(
    {
      anchorPositionSec: snap.anchorPositionSec ?? snap.currentTime,
      anchorServerTimeMs: snap.anchorServerTimeMs ?? snap.serverTimeMs ?? Date.now(),
      isPaused: snap.isPaused,
      timelineRevision: snap.timelineRevision ?? 1,
      durationSec: track?.durationSec ?? snap.nowPlaying?.durationSec,
    },
    Date.now(),
    clockOffsetMs,
  );

  const displaySec = resolveDisplayedPosition({
    prevDisplaySec: prev.currentTime,
    nextSec: interpolated,
    prevTimelineRevision: lastTimelineRevision || (snap.timelineRevision ?? 1),
    nextTimelineRevision: snap.timelineRevision ?? 1,
    isPaused: snap.isPaused,
  });
  lastTimelineRevision = snap.timelineRevision ?? 1;

  applyingRemote = true;
  try {
    usePlayer.setState({
      queue: hasServerQueue ? queue : prev.queue,
      index,
      currentTime: displaySec,
      isPlaying,
      activeOwners: { ...snap.activeOwners },
      normalize: snap.normalization?.enabled ?? prev.normalize,
      normalizeGainDb: snap.normalization?.preampDb ?? prev.normalizeGainDb,
      timelineRevision: snap.timelineRevision ?? 1,
      duration: trackChanged
        ? (track?.durationSec ?? snap.nowPlaying?.durationSec ?? null)
        : (track?.durationSec ??
            snap.nowPlaying?.durationSec ??
            prev.duration),
    });
    syncAudioElement(snap, track, isPlaying, displaySec, timelineChanged, userInitiated);
  } finally {
    applyingRemote = false;
    if (userInitiated) clearLocalPlaybackControl();
  }
}

export function applyPlaybackApiSnapshot(
  snap: PlaybackSnapshot | null | undefined,
  userInitiated = false,
): void {
  if (snap) applyPlaybackSnapshot(snap, tracksCatalog, userInitiated);
}

/** Tick displayed progress from the latest absolute snapshot (rAF / interval). */
export function tickPlaybackProgress(nowClientMs = Date.now()): number | null {
  const snap = latestSnapshot;
  if (!snap) return null;
  const next = interpolatePosition(
    {
      anchorPositionSec: snap.anchorPositionSec ?? snap.currentTime,
      anchorServerTimeMs: snap.anchorServerTimeMs ?? snap.serverTimeMs ?? nowClientMs,
      isPaused: snap.isPaused,
      timelineRevision: snap.timelineRevision ?? 1,
      durationSec: snap.nowPlaying?.durationSec,
    },
    nowClientMs,
    clockOffsetMs,
  );
  const prev = usePlayer.getState().currentTime;
  const display = resolveDisplayedPosition({
    prevDisplaySec: prev,
    nextSec: next,
    prevTimelineRevision: lastTimelineRevision,
    nextTimelineRevision: snap.timelineRevision ?? 1,
    isPaused: snap.isPaused,
  });
  if (Math.abs(prev - display) >= 0.05) {
    usePlayer.setState({ currentTime: display });
  }
  return display;
}

/** @internal test helper */
export function _resetPlaybackSyncForTests(): void {
  applyingRemote = false;
  lastRevision = -1;
  lastTimelineRevision = 0;
  latestSnapshot = null;
  tracksCatalog = new Map();
  localControlUntilMs = 0;
  clockOffsetMs = 0;
  clockRttMs = null;
}

/** @internal test helper */
export function _isUnderLocalControlForTests(): boolean {
  return isUnderLocalControl();
}
